import React from 'react';
import { LineChart, Line, Legend, YAxis, XAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import { makeStyles, darken } from '@material-ui/core/styles';

import { useI18n, SHORT_MONTH_AND_YEAR_FORMAT, MONTH_AND_YEAR_FORMAT } from '@hydra/common/components/i18n';
import { Dot } from '@hydra/common/components';
import { Criterium, CriteriumSkeleton } from '@hydra/common/criterium';
import { useReviewContext, usePdfTemplateContext } from '@hydra/common/context';
import DotChart from '@hydra/common/criterium/CriteriumChart/components/Dot';

import { getContrastedColor, getPriSecColors } from '@hydra/utils/colors';
import hasCritData from '@hydra/utils/hasCritData';

import { CriteriumInterface, ReviewData } from '@hydra/types/review';

const MONTH_VISITS_TO_SHOW = 6;

// using width 99% to make container resize properly https://github.com/recharts/recharts/issues/172#issuecomment-307858843
const CHART_OPTIONS = {
  width: '99%',
  height: 300
};

const useStyles = makeStyles(theme => ({
  table: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[200]}`,
    display: 'block',
    padding: theme.spacing(2),
    borderSpacing: '5px',
    borderCollapse: 'separate'
  },
  th: { textAlign: 'center' },
  left: { textAlign: 'left' },
  right: { textAlign: 'right' },
  legendSpan: {
    marginRight: theme.spacing(0.75),
    color: darken(theme.palette.grey[900], 0.3)
  }
}));

interface CustomTooltipProps {
  payload?: {
    payload: any
    color: string
    dataKey: string
  }[]
  label?: string
  formatter?: (value: string) => string
  labelFormatter?: (label: string) => string
}

function CustomTooltip ({ payload = [], formatter = () => '', label = '', labelFormatter = () => '' }: CustomTooltipProps) {
  const classes = useStyles();
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th className={classes.th} colSpan={3}>{labelFormatter(label)}</th>
        </tr>
      </thead>
      <tbody>
        {payload.map(({ payload, color, dataKey }, i) => {
          return (
            <tr key={i}>
              <td className={classes.left}>
                <Dot size='sm' color={color} />
              </td>
              <td className={classes.left}>
                {dataKey}
              </td>
              <td className={classes.right}>
                {formatter(payload[dataKey])}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

interface MonthlyVisit {
  date: string
  value: number
}

interface TrafficMonthlyVisits extends CriteriumInterface {
  data: {
    visits: MonthlyVisit[] | null
  }
}

export const TrafficMonthlyVisits = ({ criterium, competitors = [] }: { criterium: TrafficMonthlyVisits, competitors: Array<ReviewData> }) => {
  const classes = useStyles();
  const { moment, numeral } = useI18n();

  const { isPdf, variant } = useReviewContext();
  const { templateColors } = usePdfTemplateContext();

  if (variant === 'nocrit') {
    return (
      <Criterium criterium={criterium}>
        <CriteriumSkeleton variant='rect' style={{ height: '150px' }} />
      </Criterium>
    );
  }

  const { primary, secondary } = isPdf
    ? templateColors
    : getPriSecColors();
  const color = getContrastedColor(primary);

  if (!criterium.data.visits || criterium.data.visits.length === 0) {
    return null;
  }

  const colors = { [criterium.url]: color };

  const data = criterium.data.visits
    .map(visit => {
      const result = {
        date: (new Date(visit.date)).getTime(),
        [criterium.url]: visit.value
      };
      competitors.forEach((competitor, index) => {
        if (competitor.criteria && hasCritData(competitor.criteria.traffic_monthly_visits)) {
          const { url, data } = competitor.criteria.traffic_monthly_visits;
          const competitorVisits = data.visits;
          const competitorVisit = competitorVisits.find(({ date }: { date: string }) => date === visit.date);
          result[url] = competitorVisit ? competitorVisit.value : undefined;
          colors[url] = secondary[index];
        }
      });
      return result;
    })
    .sort((a, b) => {
      if (a.date < b.date) { return 1; }
      if (a.date > b.date) { return -1; }
      return 0;
    })
    .slice(0, MONTH_VISITS_TO_SHOW)
    .sort((a, b) => {
      if (a.date < b.date) { return -1; }
      if (a.date > b.date) { return 1; }
      return 0;
    });

  const shortDateFormatter = (unixTime: number) => {
    if (!unixTime) {
      return '';
    }
    return moment(unixTime).format(SHORT_MONTH_AND_YEAR_FORMAT[moment().locale() as keyof typeof SHORT_MONTH_AND_YEAR_FORMAT]);
  };

  const dateFormatter = (unixTime: number) => {
    if (!unixTime) {
      return '';
    }
    return moment(unixTime).format(MONTH_AND_YEAR_FORMAT[moment().locale() as keyof typeof MONTH_AND_YEAR_FORMAT]);
  };

  const legendFormatter = (value: string) => {
    return <span className={classes.legendSpan}>{value}</span>;
  };

  const tickFormatter = (tick: number) => {
    return numeral(tick || 0).format('0.[0]a');
  };

  const content = (
    // using width 99% to make container resize properly https://github.com/recharts/recharts/issues/172#issuecomment-307858843
    <ResponsiveContainer {...CHART_OPTIONS}>
      <LineChart data={data} margin={{ top: 20 }}>
        <XAxis
          dataKey='date'
          tickFormatter={shortDateFormatter}
          interval={0}
          axisLine={false}
          tick={{ transform: 'translate(0, 5)', fontSize: 12 }}
          tickLine={false}
        />
        <YAxis
          tickFormatter={tickFormatter}
          axisLine={false}
          domain={[0, 'auto']}
          interval={0}
          tick={{ transform: 'translate(-5, 0)', fontSize: 12 }}
          tickLine={false}
        />
        <CartesianGrid vertical={false} />
        <Legend wrapperStyle={{ bottom: -10 }} formatter={legendFormatter} />
        {
          Object.keys(colors).map((key) => (
            <Line
              key={key}
              isAnimationActive={false}
              dot={<DotChart stroke={colors[key]} />}
              dataKey={key}
              type='monotone'
              stroke={colors[key]}
              strokeWidth='2'
            />
          ))
        }
        <Tooltip
          cursor={false}
          labelFormatter={dateFormatter}
          formatter={tickFormatter}
          content={<CustomTooltip />}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  return <Criterium criterium={criterium}>{content}</Criterium>;
};

export default React.memo(TrafficMonthlyVisits);
